import React, { useCallback, useState } from "react";
import { Text } from "@gcs/design-system/react";
import Recaptcha from "../../common/Form/Recaptcha";

import InputFile from "./FormInputs/InputFile";
import InputText from "./FormInputs/InputText";
import Select from "./FormInputs/Select";
import TextArea from "./FormInputs/TextArea";
import RadioButton from "./FormInputs/RadioButton";
import SpinnerLoading from "components/common/SpinnerLoading";

import useValidation from "hooks/useValidation";

import {
  required,
  cpfCnpjValidation,
  phoneValidation,
  emailValidation,
  nameValidation,
  validateMinLength,
} from "utils/validators";

import { authenticate } from "services/Auth.service";
import {
  checkOmbudsmanProtocol,
  sendOmbudsmanProtocol,
} from "services/Ombudsman.service";

import { OmbudsmanData } from "utils/ombudsmanData";

import { ErrorMessage } from "components/ErrorMessage/ErrorMessage.style";
import * as S from "./OmbudsmanForm.styled";

const PROTOCOL_LENGTH = 12;
const DETAILS_MAX_LENGTH = 2000;
const DETAILS_MIN_LENGTH = 50;
const caixaSeguradora = "Caixa Seguradora";

const ifCondition = (condition, ifTrue, ifFalse) => {
  if (condition) {
    return ifTrue;
  }

  return ifFalse;
};

const ifCallback = (
  condition,
  callback1,
  callback2 = () => {
    return;
  },
) => {
  if (condition) {
    return callback1();
  }

  return callback2();
};

const andCompare = (one, two) => {
  return one && two;
};

const getProvidedFiles = (mandatoryFiles, optional, procuration) => {
  const files = mandatoryFiles;

  if (optional?.binario) {
    files.push(optional);
  }
  if (procuration?.binario) {
    files.push(procuration);
  }

  return files;
};

const OmbudsmanForm = ({
  protocolData,
  formError,
  setResponseOmbudsmanData,
  setResponseOmbudsmanError,
  setResponseProtocolError,
}) => {
  const [isShowFields, setIsShowFields] = useState(false);
  const [isLoadingProtocol, setIsLoadingProtocol] = useState(false);
  const [isLoadingSendOmbudsman, setIsLoadingSendOmbudsman] = useState(false);

  /* Fields */
  const [selectedCompany, setSelectedCompany] = useState({
    value: "",
    label: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [selectedProduct, setSelectedProduct] = useState({
    value: "",
    label: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [protocol, setProtocol] = useState({
    value: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [details, setDetails] = useState({
    value: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [optionalFile, setOptionalFile] = useState(null);

  const [name, setName] = useState({
    value: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [cpfCnpj, setCpfCnpj] = useState({
    value: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [phone, setPhone] = useState({
    value: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [email, setEmail] = useState({
    value: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [frontDocument, setFrontDocument] = useState({
    value: null,
    valid: false,
    errorMessage: "",
  });

  const [backDocument, setBackDocument] = useState({
    value: null,
    valid: false,
    errorMessage: "",
  });

  const [procurationFile, setProcurationFile] = useState(null);

  const [selectedContactWay, setSelectedContatctWay] = useState({
    value: "",
    touched: false,
    valid: false,
    errorMessage: "",
  });

  const [recaptcha, setRecaptcha, validRecaptcha, setValidRecaptcha] =
    useValidation("");

  const validate = () => {
    let isAllValid = true;
    let idToScroll = "";

    const fieldsToValidate = [
      {
        value: selectedCompany.value,
        setValid: (valid, errorMessage) =>
          setSelectedCompany((n) => ({ ...n, valid, errorMessage })),
        id: "Company",
        validations: [required],
      },
      {
        value: protocol.value,
        setValid: (valid, errorMessage) =>
          setProtocol((n) => ({ ...n, valid, errorMessage, touched: true })),
        id: "Protocol",
        validations: [(value) => validateMinLength(value, PROTOCOL_LENGTH)],
      },
      {
        value: details.value,
        setValid: (valid, errorMessage) =>
          setDetails((d) => ({ ...d, valid, errorMessage, touched: true })),
        id: "Details",
        validations: [(value) => validateMinLength(value, 50)],
      },
      {
        value: name.value,
        setValid: (valid, errorMessage) =>
          setName((n) => ({ ...n, valid, errorMessage, touched: true })),
        id: "Name",
        validations: [nameValidation],
      },
      {
        value: cpfCnpj.value,
        setValid: (valid, errorMessage) =>
          setCpfCnpj((c) => ({ ...c, valid, errorMessage, touched: true })),
        id: "CpfCnpj",
        validations: [cpfCnpjValidation],
      },
      {
        value: frontDocument.value,
        setValid: (valid, errorMessage) =>
          setFrontDocument((mf) => ({
            ...mf,
            valid,
            errorMessage,
            touched: true,
          })),
        id: "frontDocument",
        validations: [required],
      },
      {
        value: backDocument.value,
        setValid: (valid, errorMessage) =>
          setBackDocument((mfValue) => ({
            ...mfValue,
            valid,
            errorMessage,
            touched: true,
          })),
        id: "backDocument",
        validations: [required],
      },
      {
        value: selectedContactWay.value,
        setValid: (valid, errorMessage) =>
          setSelectedContatctWay((cw) => ({
            ...cw,
            valid,
            errorMessage,
            touched: true,
          })),
        id: "Contact",
        validations: [required],
      },
      {
        value: phone.value,
        setValid: (valid, errorMessage) =>
          setPhone((p) => ({ ...p, valid, errorMessage, touched: true })),
        id: "Phone",
        validations: [phoneValidation],
      },
      {
        value: email.value,
        setValid: (valid, errorMessage) =>
          setEmail((e) => ({ ...e, valid, errorMessage, touched: true })),
        id: "Email",
        validations: [emailValidation],
      },
      {
        value: recaptcha,
        setValid: setValidRecaptcha,
        id: "Recaptcha",
        validations: [required],
      },
    ];
    if (selectedCompany.value === caixaSeguradora) {
      fieldsToValidate.unshift({
        value: selectedProduct.value,
        id: "Product",
        validations: [required],
        setValid: (valid, errorMessage) =>
          setSelectedProduct((n) => ({ ...n, valid, errorMessage })),
      });
    }
    fieldsToValidate.forEach(({ value, setValid, id, validations }) => {
      const noError = () => ({ valid: true, errorMessage: "" });
      const { valid, errorMsg } = validations.find(
        (v) => !v(value).valid || noError,
      )(value);
      setValid(valid, errorMsg);
      ifCallback(andCompare(!valid, idToScroll === ""), () => {
        idToScroll = id;
        isAllValid = false;
      });
      ifCallback(idToScroll !== "", () => {
        document
          .getElementById(idToScroll)
          .scrollIntoView({ block: "center", behavior: "smooth" });
      });
    });
    return isAllValid;
  };

  const handleRecaptcha = (event) => {
    setRecaptcha(event);
    authenticate();
  };

  const handleSubmitCheckProtocol = useCallback(async () => {
    if (
      !selectedProduct?.value ||
      !selectedCompany?.value ||
      !protocol?.value
    ) {
      return;
    }

    setIsLoadingProtocol(true);

    try {
      await authenticate();

      const response = await checkOmbudsmanProtocol({
        protocolo: protocol.value,
        produto: selectedProduct.value,
      });

      setIsShowFields(response?.data?.isSuccess);
    } catch (err) {
      setResponseProtocolError({ error: err });
    } finally {
      setIsLoadingProtocol(false);
    }
  }, [
    protocol.value,
    selectedCompany?.value,
    selectedProduct.value,
    setResponseProtocolError,
  ]);

  const handleSubmitSendOmbudsmanProtocol = async () => {
    if (!validate()) {
      return;
    }

    if (!recaptcha) {
      return;
    }

    try {
      setIsLoadingSendOmbudsman(true);

      const response = await sendOmbudsmanProtocol(
        {
          protocolo: {
            produto: selectedProduct.value,
            numero: protocol.value,
          },
          solicitante: {
            nome: name.value,
            email: email.value,
            tipoRetorno: selectedContactWay.value,
            cpfCnpj: {
              numero: cpfCnpj.value,
              tipo: ifCondition(cpfCnpj.value.length === 11, "CPF", "CNPJ"),
            },
            telefone: {
              ddd: phone.value.substring(0, 2),
              numero: phone.value.substring(2),
            },
          },
          comentario: details.value,
          anexos: getProvidedFiles(
            [
              {
                nome: frontDocument.value?.name,
                binario: frontDocument.value?.base64,
                descricao: "Frente do documento",
              },
              {
                nome: backDocument.value?.name,
                binario: backDocument.value?.base64,
                descricao: "Verso do documento",
              },
            ],
            {
              nome: optionalFile?.name,
              binario: optionalFile?.base64,
              descricao: "Anexo opcional ouvidoria",
            },
            {
              nome: procurationFile?.name,
              binario: procurationFile?.base64,
              descricao: "Procuração",
            },
          ),
        },
        recaptcha,
      );

      setResponseOmbudsmanData(response);
    } catch (err) {
      setResponseOmbudsmanError({ error: err });
    } finally {
      setIsLoadingSendOmbudsman(false);
      window.grecaptcha.reset();
    }
  };

  return (
    <S.FormContainer>
      <Select
        id="Company"
        selected={selectedCompany}
        disabled={andCompare(protocolData === "", selectedCompany.value !== "")}
        setSelected={(selectCompany) => {
          setSelectedCompany(selectCompany);
          const companyValue = selectCompany(selectedCompany).value;
          let productValue = "";

          if (companyValue === "Youse") {
            productValue = "Youse";
          } else if (companyValue === "Previsul") {
            productValue = "Previsul";
          }

          setSelectedProduct((product) => ({
            ...product,
            value: productValue,
            label: productValue,
          }));
        }}
        options={OmbudsmanData.companyOptions}
        errorMessage={selectedCompany.errorMessage}
        placeholder="Selecione a empresa"
        title="Sobre qual empresa?"
      />

      {selectedCompany.value === caixaSeguradora && (
        <Select
          disabled={andCompare(
            protocolData === "",
            selectedProduct.value !== "",
          )}
          id="Product"
          options={OmbudsmanData.cnpProducts}
          selected={selectedProduct}
          setSelected={setSelectedProduct}
          placeholder="Selecione o produto"
          title="Sobre qual produto?"
          errorMessage={selectedProduct.errorMessage}
        />
      )}

      {selectedCompany.value === "YOUSE" && (
        <div>
          <S.InfoTitle>Ouvidoria Youse</S.InfoTitle>
          <S.InfoText>
            <a href="tel:08007309991">0800 730 9991</a>
            <p>
              Seu problema não foi resolvido nos canais de atendimento? Ligue
              para a Ouvidoria: segunda a sexta, de 8h as 18h, exceto em
              feriados.
            </p>
          </S.InfoText>
        </div>
      )}

      {selectedCompany.value === caixaSeguradora && selectedProduct.value && (
        <InputText
          id="Protocol"
          onChange={(e) =>
            setProtocol((p) => ({ ...p, value: e.target.value }))
          }
          maxLength={12}
          value={protocol.value}
          placeholder="Digite o protocolo"
          title="Informe o número do protocolo anterior"
          mask={"9".repeat(PROTOCOL_LENGTH)}
          maskPlaceholder=""
          hasError={andCompare(protocol.touched, !protocol.valid)}
          errorMessage={`O campo protocolo é obrigatório e deve ter ${PROTOCOL_LENGTH} caracteres`}
          disabled={protocolData === ""}
          onBlur={() =>
            setProtocol((p) => ({
              ...p,
              touched: true,
              valid: p.value.length === PROTOCOL_LENGTH,
            }))
          }
        />
      )}

      {isLoadingProtocol && <SpinnerLoading alone />}

      {protocolData !== "" && selectedCompany.value !== "YOUSE" && (
        <S.StyledButton
          onClick={handleSubmitCheckProtocol}
          disabled={
            protocol.value.length !== PROTOCOL_LENGTH || isLoadingProtocol
          }
        >
          Verificar Protocolo
        </S.StyledButton>
      )}

      {isShowFields && (
        <>
          <TextArea
            id="Details"
            title="Escreva com detalhes e clareza sua ouvidoria*"
            onChange={(e) =>
              setDetails((d) => ({ ...d, value: e.target.value }))
            }
            value={details.value}
            maxLength={DETAILS_MAX_LENGTH}
            minLength={DETAILS_MIN_LENGTH}
            hasError={andCompare(details.touched, !details.valid)}
            errorMessage={details.errorMessage}
            onBlur={() => {
              const isValid = details.value.length >= DETAILS_MIN_LENGTH;
              setDetails((d) => ({
                ...d,
                touched: true,
                valid: isValid,
                errorMessage: validateMinLength(d.value, 50).errorMsg,
              }));
            }}
            tip={`${
              DETAILS_MAX_LENGTH - details.value?.length
            } caracteres restantes`}
            placeholder="Escreva aqui"
          />
          <p>
            Quer anexar algum arquivo? Ele ajuda a entender melhor sua
            solicitação.
          </p>
          <InputFile
            title="Anexar arquivo (opcional)"
            details="Máximo 600 KB em formatos JPG, JPEG, PNG, GIF, BMP, PDF, DOC, DOCX, XLS ou XLSX."
            file={optionalFile}
            setFile={setOptionalFile}
            maxSize={600}
          />
          <Text variant="headline-03" color="primary">
            Dados pessoais
          </Text>
          <S.SubTitle variant="headline-08">
            Pedimos seus dados apenas para a análise e resposta da sua
            ouvidoria.
          </S.SubTitle>
          <S.TwoInputContainer>
            <S.InputName
              id="Name"
              onChange={(e) =>
                setName((n) => ({ ...n, value: e.target.value }))
              }
              value={name.value}
              title="Qual seu nome?*"
              placeholder="Digite seu nome completo"
              hasError={andCompare(name.touched, !name.valid)}
              errorMessage={nameValidation(name.value).errorMsg}
              onBlur={() =>
                setName((p) => ({
                  ...p,
                  touched: true,
                  valid: nameValidation(name.value).valid,
                }))
              }
            />
            <S.InputCpfCnpj
              id="CpfCnpj"
              name="cpfCnpj"
              onChange={(e) =>
                setCpfCnpj((n) => ({
                  ...n,
                  value: e.target.value.replace(/[^\d]/g, ""),
                }))
              }
              value={cpfCnpj.value}
              title="Informe seu CPF ou CNPJ*"
              placeholder="Digite um CPF ou CNPJ válido"
              mask={ifCondition(
                cpfCnpj.value.length < 12,
                "999.999.999-999",
                "99.999.999/9999-99",
              )}
              maskPlaceholder=""
              hasError={cpfCnpj.touched && !cpfCnpj.valid}
              errorMessage={cpfCnpjValidation(cpfCnpj.value).errorMsg}
              onBlur={() =>
                setCpfCnpj((c) => ({
                  ...c,
                  touched: true,
                  valid: cpfCnpjValidation(c.value).valid,
                }))
              }
            />
          </S.TwoInputContainer>
          <Text variant="body02-md">
            <strong>
              Documento de indentidade oficial com foto (RG, CNH ou passaporte)
            </strong>
            <br />
            <i>
              Caso esteja representando outra pessoa, anexe também o documento
              da procuração
            </i>
          </Text>
          <S.FileContainer>
            <InputFile
              id="frontDocument"
              title="Frente do documento"
              subTitle="RG, CNH ou RNE"
              details="Tipo de Arquivo JPG, JPEG, PNG, GIF, BMP, PDF, DOC, DOCX, XLS ou XLSX."
              file={frontDocument.value}
              maxSize={600}
              errorMessage={frontDocument.errorMessage}
              setFile={(newFile) =>
                setFrontDocument((file) => ({ ...file, value: newFile }))
              }
            />
            <InputFile
              id="backDocument"
              title="Verso do documento"
              subTitle="RG, CNH ou RNE"
              details="Tipo de Arquivo JPG, JPEG, PNG, GIF, BMP, PDF, DOC, DOCX, XLS ou XLSX."
              file={backDocument.value}
              maxSize={600}
              errorMessage={backDocument.errorMessage}
              setFile={(newFile) =>
                setBackDocument((file) => ({ ...file, value: newFile }))
              }
            />
            <InputFile
              id="procurationFile"
              title="Procuração (opcional)"
              subTitle="RG, CNH ou RNE"
              details="Tipo de Arquivo JPG, JPEG, PNG, GIF, BMP, PDF, DOC, DOCX, XLS ou XLSX."
              file={procurationFile}
              maxSize={600}
              setFile={setProcurationFile}
            />
          </S.FileContainer>
          <RadioButton
            id="Contact"
            title={"Como deseja receber o retorno da sua ouvidoria?*"}
            options={OmbudsmanData.contactWays.filter((contact) => {
              return !(
                selectedCompany.value === "YOUSE" &&
                contact.value === "WhatsApp"
              );
            })}
            setSelected={setSelectedContatctWay}
            hasError={selectedContactWay.touched && !selectedContactWay.valid}
            errorMessage={selectedContactWay.errorMessage}
            selected={selectedContactWay.value}
          />
          <S.TwoInputContainer>
            <S.InputPhone
              id="Phone"
              onChange={(e) =>
                setPhone((n) => ({
                  ...n,
                  value: e.target.value.replace(/[^\d]/g, ""),
                }))
              }
              title={`Qual seu número de ${ifCondition(
                selectedContactWay.value === "WhatsApp",
                "celular",
                "telefone",
              )}?*`}
              value={phone.value}
              placeholder="Digite com o DDD"
              mask={ifCondition(
                phone.value?.length > 10 ||
                  selectedContactWay.value === "WhatsApp",
                "(99) 99999-9999",
                "(99) 9999-99999",
              )}
              maskPlaceholder=""
              hasError={phone.touched && !phone.valid}
              errorMessage={phoneValidation(phone.value).errorMsg}
              onBlur={() =>
                setPhone((oldP) => ({
                  ...oldP,
                  touched: true,
                  valid: phoneValidation(oldP.value).valid,
                }))
              }
            />
            <S.InputEmail
              id="Email"
              onChange={(e) =>
                setEmail((n) => ({ ...n, value: e.target.value }))
              }
              value={email.value}
              hasError={andCompare(email.touched, !email.valid)}
              title="Qual o seu e-mail?*"
              placeholder="exemplo@exemplo.com"
              errorMessage={emailValidation(email.value).errorMsg}
              onBlur={() =>
                setEmail((e) => ({
                  ...e,
                  touched: true,
                  valid: emailValidation(e.value).valid,
                }))
              }
            />
          </S.TwoInputContainer>
          <Text variant="caption-02" style={{ marginBottom: "40px" }}>
            {" "}
            * O e-mail deve ser informado em todos os casos, independente dos
            canais para retorno já escolhidos.{" "}
          </Text>
          {navigator?.userAgent !== "ReactSnap" && (
            <>
              {" "}
              <Recaptcha
                id="Recaptcha"
                size="normal"
                onChange={handleRecaptcha}
              />
              <S.ErrorLabel variant="body02-sm" error={!validRecaptcha?.valid}>
                {validRecaptcha?.errorMsg}
              </S.ErrorLabel>
            </>
          )}
          {formError?.response?.status && !isLoadingSendOmbudsman && (
            <ErrorMessage>Erro ao abrir ouvidoria</ErrorMessage>
          )}
          <S.StyledButton
            type="button"
            onClick={handleSubmitSendOmbudsmanProtocol}
            disabled={!!isLoadingSendOmbudsman}
          >
            Abrir Ouvidoria
          </S.StyledButton>
          {isLoadingSendOmbudsman && <SpinnerLoading alone />}
        </>
      )}
    </S.FormContainer>
  );
};

export default OmbudsmanForm;
